import { ChangeDetectorRef, Directive, forwardRef, inject, OnDestroy, Provider } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subject } from 'rxjs';

import { isBoolean } from 'lodash-es';
import { FocusManagerService } from '../core/common/services/focus-manager.service';

export function MakeProvider(type: any): Provider {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => type),
        multi: true
    };
}

@Directive()
export abstract class BaseControlValueAccessor implements ControlValueAccessor, OnDestroy {
    protected readonly componentId: string = `${this.constructor.name}-${Math.random().toString(36).slice(0, 9)}`;
    public disabled = false;
    protected destroy$: Subject<void> = new Subject<void>();
    private delayedChange: any;

    private readonly focusManager: FocusManagerService = inject(FocusManagerService);

    constructor(protected changeDetectorRef: ChangeDetectorRef) {}

    changeHandler(_: any): void {
        this.delayedChange = _;
    }

    touchHandler(_: any): void {
        return;
    }

    propagateChange(_: any): void {
        this.changeHandler(_);
        this.changeDetectorRef.detectChanges();
    }

    onTouched(_: any): void {
        this.touchHandler(_);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        // https://github.com/angular/angular/issues/27803#issuecomment-451975496
        // manually clearing callbacks
        this.propagateChange = () => {};
        this.changeHandler = () => {};
        this.touchHandler = () => {};
        this.writeValue = () => {};
        this.setDisabledState = () => {};
    }

    registerOnChange(fn: any): void {
        this.changeHandler = fn;
        if (!!this.delayedChange || isBoolean(this.delayedChange)) {
            fn(this.delayedChange);
            this.delayedChange = null;
            this.changeDetectorRef.detectChanges();
        }
    }

    registerOnTouched(fn: any): void {
        this.touchHandler = fn;
    }

    abstract writeValue(obj: any): void;

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.changeDetectorRef.detectChanges();
    }

    protected activate() {
        this.focusManager.setActive(this.componentId);
    }

    protected deactivate() {
        this.focusManager.clearActive();
    }

    protected isActiveComponent(): boolean {
        return this.focusManager.currentActive === this.componentId;
    }
}
